import { Injectable } from '@angular/core';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { GoogleMapsLoaderService } from './google-maps-loader.service';

export interface IGoogleMapsConfig {
  styles: any[];
  options: any;
  markerOptions: any;
}

export interface IGmapData {
  lat: number;
  lng: number;
  label: string;
  description: string;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsConfigService {
  
  constructor(private googleMapsLoader: GoogleMapsLoaderService) {}

  /**
   * Default map styles used across the application
   */
  private readonly defaultMapStyles: any[] = [
    {
      featureType: 'landscape.natural',
      elementType: 'geometry',
      stylers: [{ color: '#EAE8E1' }],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#A9D1F0' }],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{ color: '#F9D69B' }],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry',
      stylers: [{ color: '#D3D3D3' }],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [{ color: '#A9D1A0' }],
    },
    {
      featureType: 'all',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#4E4E4E' }],
    },
    {
      featureType: 'all',
      elementType: 'labels.text.stroke',
      stylers: [{ color: '#FFFFFF' }],
    },
  ];

  /**
   * Get default map styles
   */
  getMapStyles(): any[] {
    return [...this.defaultMapStyles];
  }

  /**
   * Get default map options
   * @param customOptions - Optional custom options to override defaults
   */
  getMapOptions(customOptions: Partial<any> = {}): any {
    const defaultOptions = {
      zoom: 16,
      disableDefaultUI: true,
      styles: this.getMapStyles(),
    };

    return { ...defaultOptions, ...customOptions };
  }

  /**
   * Get map marker options (requires Google Maps API to be loaded)
   * @param customOptions - Optional custom options to override defaults
   */
  async getMapMarkerOptions(customOptions: Partial<any> = {}): Promise<any> {
    await this.googleMapsLoader.loadGoogleMaps();
    
    const google = (window as any).google;
    const defaultMarkerOptions = {
      animation: google.maps.Animation.DROP,
      icon: STRING_CONSTANTS.ICON.IC_MAP_MARKER,
    };

    return { ...defaultMarkerOptions, ...customOptions };
  }

  /**
   * Get complete Google Maps configuration
   * @param customMapOptions - Optional custom map options
   * @param customMarkerOptions - Optional custom marker options
   */
  async getCompleteConfig(
    customMapOptions: Partial<any> = {},
    customMarkerOptions: Partial<any> = {}
  ): Promise<IGoogleMapsConfig> {
    const [mapOptions, markerOptions] = await Promise.all([
      Promise.resolve(this.getMapOptions(customMapOptions)),
      this.getMapMarkerOptions(customMarkerOptions)
    ]);

    return {
      styles: this.getMapStyles(),
      options: mapOptions,
      markerOptions
    };
  }

  /**
   * Open Google Maps in a new tab with the given coordinates
   * @param coordinates - Latitude and longitude
   */
  openInGoogleMaps(coordinates: { lat: number; lng: number }): void {
    const url = `https://www.google.com/maps?q=loc:${coordinates.lat},${coordinates.lng}`;
    window.open(url, '_blank');
  }

  /**
   * Check if Google Maps API is loaded
   */
  isGoogleMapsLoaded(): boolean {
    return this.googleMapsLoader.isGoogleMapsLoaded();
  }

  /**
   * Load Google Maps API
   */
  loadGoogleMaps(): Promise<void> {
    return this.googleMapsLoader.loadGoogleMaps();
  }
}
